import { type FC, useCallback, useMemo } from 'react';
import { bulkAddUserSchema, getAdminUserDisplayName } from './types';
import { format } from 'date-fns';
import { BulkOperation } from '../BulkOperation/BulkOperation';
import { z } from 'zod';
import { HttpError, useDataProvider } from 'ra-core';
import { type AdminDataProvider } from '../../api/adminDataProvider';
import {
  type GridRowValidator,
  type GridRowValidationError,
  type RowProcessor,
} from '../BulkOperation/types';
import { superRefineDuplicateProps } from '../BulkOperation/bulkZodUtils';
import { getErrorMessage } from './getErrorMessage';
import { useTenantId } from '../../components/OrganizationGuard';

export const AddUsersButton: FC = () => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const tenantId = useTenantId();

  const csvRowSchema = useMemo(
    () =>
      bulkAddUserSchema.extend({
        // We do this to ensure the user doesn't accidentally upload the CSV to the wrong tenant
        // https://www.notion.so/33628688acde8064a14de8419df158e9
        tenantId: z.literal(tenantId),
      }),
    [tenantId]
  );

  const exampleValues = [
    {
      internalId: 'example-id',
      firstName: 'John',
      lastName: 'Smith',
      phoneNumber: '+40123456789',
      email: 'example@gmail.com',
      startDate: format(new Date(), 'dd-MM-yyyy'),
      department: 'HR',
      jobTitle: 'Manager',
      region: 'UK',
      unit: 'London',
      languageCountryCode: 'en',
      tenantId,
    },
  ] as const satisfies z.infer<typeof csvRowSchema>[];

  const csvSchema = useMemo(
    () =>
      z.array(csvRowSchema).superRefine((allRows, ctx) => {
        superRefineDuplicateProps('phoneNumber', allRows, ctx);
        superRefineDuplicateProps(
          'internalId',
          allRows.filter((r) => !!r.internalId),
          ctx
        );
      }),
    [csvRowSchema]
  );

  const userValidator = useCallback<
    GridRowValidator<z.infer<typeof csvRowSchema>>
  >(
    async ({ data: user }) => {
      const phoneRes = await dataProvider
        .getUserByPhoneNumber(user.phoneNumber)
        .catch((error: unknown) => ({ error }));

      const errors: GridRowValidationError<z.infer<typeof csvRowSchema>>[] = [];
      if (phoneRes && 'error' in phoneRes) {
        errors.push({
          message: getErrorMessage(phoneRes.error),
          property: 'phoneNumber',
        });
      } else if (phoneRes) {
        errors.push({
          message: `This phone number is already taken by user: ${getAdminUserDisplayName(phoneRes)}`,
          property: 'phoneNumber',
        });
      }

      if (user.internalId) {
        try {
          const internalIdRes = await dataProvider.getUserByInternalId(
            user.internalId
          );

          if (internalIdRes) {
            errors.push({
              message: `This internal identifier is already taken by user: ${getAdminUserDisplayName(internalIdRes)}`,
              property: 'internalId',
            });
          }
        } catch (error) {
          errors.push({
            message: getErrorMessage(error),
            property: 'internalId',
          });
        }
      }

      return (oldData) => ({
        ...oldData,
        status: errors.length > 0 ? 'error' : 'success',
        data: user,
        asyncErrors: errors,
      });
    },
    [dataProvider]
  );

  const userProcessor = useCallback<RowProcessor<z.infer<typeof csvRowSchema>>>(
    async ({ data: user }) => {
      // Remove the tenant ID prop, as it's only used for client-side validation
      const { tenantId: _tenantId, ...userToCreate } = user;

      const res = await dataProvider
        .postUser(userToCreate)
        .catch((err: unknown) => {
          if (err instanceof HttpError) {
            // NOTE: HttpError won't return the response body, as it isn't JSON, so it ignores it
            // https://github.com/marmelab/react-admin/blob/0a7a6e7f612990dca093fbbf4cfa531ead4583a2/packages/ra-core/src/dataProvider/fetch.ts#L67
            return err;
          }

          throw err;
        });

      if (res.status === 200) {
        return (oldData) => ({
          ...oldData,
          data: user,
          status: 'success',
          errors: [],
        });
      } else {
        return (oldData) => ({
          ...oldData,
          data: user,
          status: 'error',
          errors: [
            res.body?.message ||
              'Unexpected error. Please reach out to Moonstar Support.',
          ],
        });
      }
    },
    [dataProvider]
  );

  return (
    <BulkOperation
      label="Add users"
      resource="users"
      schema={csvSchema}
      validateRow={userValidator}
      exampleValues={exampleValues}
      processRow={userProcessor}
      // due to clerk's 20reqs/10s limit
      validationRateLimitDelay={500}
      processingRateLimitDelay={500}
    />
  );
};
